import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ShieldCheck, MapPinned, Download, Save, Crosshair, Trash2, Users, FileText, Sparkles } from "lucide-react";

export default function Admin() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [range, setRange] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
  });
  const [downloading, setDownloading] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditFilter, setAuditFilter] = useState("all");

  const load = async () => {
    const [settingsRes, usersRes, auditRes] = await Promise.all([
      api.get("/settings"),
      api.get("/admin/users"),
      api.get("/admin/audit-log"),
    ]);
    setSettings(settingsRes.data);
    setUsers(usersRes.data);
    setAuditLog(auditRes.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/settings", {
        label: settings.label,
        lat: parseFloat(settings.lat),
        lng: parseFloat(settings.lng),
        radius: parseFloat(settings.radius),
      });
      toast.success("Geofence settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (p) => { setSettings({ ...settings, lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) }); toast.success("Filled with your current location"); },
      () => toast.error("Could not get location"),
      { enableHighAccuracy: true }
    );
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete ${userName}? This action cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers((current) => current.filter((item) => item.id !== userId));
      toast.success(`${userName} deleted`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    }
  };

  const downloadIncidentReport = async () => {
    try {
      const token = localStorage.getItem("dp_token");
      const res = await fetch(`${API}/admin/incidents/report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("All reports export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "incident_reports.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("All incident reports downloaded");
    } catch (err) {
      toast.error("Could not download incident report");
    }
  };

  const download = async () => {
    const start = new Date(range.start);
    const end = new Date(range.end);
    const maxBack = new Date(end);
    maxBack.setMonth(maxBack.getMonth() - 3);
    if (start < maxBack) return toast.error("Range cannot exceed 3 months");
    if (start > end) return toast.error("Start date must be before end date");
    setDownloading(true);
    try {
      const token = localStorage.getItem("dp_token");
      const res = await fetch(`${API}/admin/report?start=${range.start}&end=${range.end}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Report failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${range.start}_to_${range.end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV report downloaded");
    } catch (err) {
      toast.error("Could not download report");
    } finally {
      setDownloading(false);
    }
  };

  if (!settings) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Admin Control
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">Administration</h1>
        <p className="text-muted-foreground mt-1">Configure the attendance geofence and export reports.</p>
        <Button asChild variant="outline" className="mt-4" data-testid="edit-seasonal-banner-link">
          <Link to="/admin/seasonal-banner"><Sparkles className="mr-2 h-4 w-4" />Edit welcome banner</Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 border-t-4 border-sky-500 pt-4">
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-lg font-bold flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" /> Registered users
            </h2>
            <span className="text-sm text-muted-foreground">{users.length} total</span>
          </div>
          <div className="space-y-3">
            {users.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary/40 px-4 py-3">
                <div>
                  <p className="font-semibold">{entry.name}</p>
                  <p className="text-xs text-muted-foreground">{entry.email} • {entry.role}</p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteUser(entry.id, entry.name)}
                  disabled={entry.role === "admin"}
                  data-testid={`delete-user-${entry.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Geofence config */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-5">
            <MapPinned className="h-5 w-5 text-primary" /> Geofence Location
          </h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Location name</Label>
              <Input value={settings.label} onChange={(e) => setSettings({ ...settings, label: e.target.value })} data-testid="geofence-label-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Latitude</Label>
                <Input type="number" step="any" value={settings.lat} onChange={(e) => setSettings({ ...settings, lat: e.target.value })} data-testid="geofence-lat-input" />
              </div>
              <div className="space-y-2">
                <Label>Longitude</Label>
                <Input type="number" step="any" value={settings.lng} onChange={(e) => setSettings({ ...settings, lng: e.target.value })} data-testid="geofence-lng-input" />
              </div>
            </div>
            <Button variant="secondary" size="sm" onClick={useMyLocation} data-testid="use-my-location-btn">
              <Crosshair className="h-4 w-4 mr-1" /> Use my current location
            </Button>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Allowed radius</Label>
                <span className="text-sm font-semibold" data-testid="radius-value">{Math.round(settings.radius)} m</span>
              </div>
              <Slider value={[Number(settings.radius)]} min={20} max={1000} step={10} onValueChange={(v) => setSettings({ ...settings, radius: v[0] })} data-testid="geofence-radius-slider" />
            </div>
            <Button onClick={save} className="w-full" data-testid="save-geofence-btn">
              <Save className="h-4 w-4 mr-2" /> Save settings
            </Button>
          </div>
        </div>

        {/* CSV export */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-5">
            <Download className="h-5 w-5 text-primary" /> Attendance Report (CSV)
          </h2>
          <p className="text-sm text-muted-foreground mb-5">
            Export check-in/out records. Range up to 3 months. Times are in Malaysia/Kuala Lumpur (12-hour) with exact coordinates.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>From</Label>
              <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="report-start-input" />
            </div>
            <div className="space-y-2">
              <Label>To</Label>
              <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="report-end-input" />
            </div>
          </div>
          <Button onClick={download} disabled={downloading} className="w-full mt-5" data-testid="admin-export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> {downloading ? "Preparing…" : "Download CSV report"}
          </Button>
          <Button onClick={downloadIncidentReport} variant="secondary" className="w-full mt-3" data-testid="admin-export-incident-report-btn">
            <FileText className="h-4 w-4 mr-2" /> Download all incident reports
          </Button>
          <div className="mt-5 rounded-lg bg-secondary p-4 text-xs text-muted-foreground">
            Columns: Resident Name, Email, Action, Date &amp; Time (MYT 12h), Latitude, Longitude, Distance from Center (m).
          </div>
        </div>
      </div>

      <section className="space-y-4 border-t-4 border-amber-500 pt-4" data-testid="admin-audit-log">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-bold">Sensitive record audit log</h2>
            <p className="mt-1 text-sm text-muted-foreground">Recent changes to student support, outing, and financial records.</p>
          </div>
          <select
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={auditFilter}
            onChange={(event) => setAuditFilter(event.target.value)}
            aria-label="Filter audit log by record type"
            data-testid="audit-log-filter"
          >
            <option value="all">All record types</option>
            <option value="student_support">Student support</option>
            <option value="outing">Outings</option>
            <option value="financial_request">Financial requests</option>
            <option value="financial_transaction">Financial ledger</option>
          </select>
        </div>
        <div className="overflow-x-auto border-y border-border">
          <table className="w-full min-w-[760px] border-collapse text-sm" data-testid="audit-log-table">
            <thead>
              <tr className="border-b border-border bg-muted/50 text-left">
                <th className="px-3 py-3 font-semibold">When</th>
                <th className="px-3 py-3 font-semibold">Record</th>
                <th className="px-3 py-3 font-semibold">Subject</th>
                <th className="px-3 py-3 font-semibold">Action</th>
                <th className="px-3 py-3 font-semibold">Changed by</th>
                <th className="px-3 py-3 font-semibold">Changes</th>
              </tr>
            </thead>
            <tbody>
              {auditLog.filter((entry) => auditFilter === "all" || entry.entity_type === auditFilter).map((entry) => (
                <tr key={entry.id} className="border-b border-border last:border-0 align-top" data-testid={`audit-log-entry-${entry.id}`}>
                  <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">
                    {new Date(entry.occurred_at).toLocaleString("en-MY", { timeZone: "Asia/Kuala_Lumpur" })}
                  </td>
                  <td className="px-3 py-3">{AUDIT_ENTITY_LABELS[entry.entity_type] || entry.entity_type}</td>
                  <td className="px-3 py-3">{entry.subject}</td>
                  <td className="px-3 py-3 capitalize">{entry.action}</td>
                  <td className="px-3 py-3">{entry.actor_name}<span className="block text-xs text-muted-foreground">{entry.actor_role}</span></td>
                  <td className="px-3 py-3">
                    {entry.changes.length > 0 ? entry.changes.map((change) => (
                      <p key={change.field}>
                        <span className="capitalize">{change.field.replace(/_/g, " ")}</span>
                        {Object.prototype.hasOwnProperty.call(change, "before") && (
                          <span className="text-muted-foreground">: {formatAuditValue(change.before)} to {formatAuditValue(change.after)}</span>
                        )}
                      </p>
                    )) : <span className="text-muted-foreground">No tracked fields</span>}
                  </td>
                </tr>
              ))}
              {auditLog.filter((entry) => auditFilter === "all" || entry.entity_type === auditFilter).length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No audit events found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const AUDIT_ENTITY_LABELS = {
  student_support: "Student support",
  outing: "Outing",
  financial_request: "Financial request",
  financial_transaction: "Financial ledger",
};

function formatAuditValue(value) {
  if (value == null || value === "") return "empty";
  return Array.isArray(value) ? value.join(", ") : String(value);
}
